import React, { useEffect, useRef, useState } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    Button,
    Stack,
    Divider,
    CircularProgress
} from "@mui/material";
import { PDFDocument } from "pdf-lib";
import Tiff from "tiff.js";
import { uploadDocuments, deleteDocument } from "../../../store/actions/documentAction";
import AppSnackbar from "../../../components/AppSnackbar";
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog'

const normalizeSplitFiles = (result) => {
    const files = [];

    if (result.extracted) {
        files.push({
            name: result.extracted.name,
            type: result.extracted.type,
            file: result.extracted
        });
    }

    if (result.remaining) {
        files.push({
            name: result.remaining.name,
            type: result.remaining.type,
            file: result.remaining
        });
    }

    return files;
};

const fetchPdfBytes = async (url) => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to fetch PDF (${response.status})`);
    }

    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("pdf")) {
        throw new Error("Invalid PDF source (not a PDF file)");
    }

    return await response.arrayBuffer();
};

async function inferPageCountByCopying(pdf) {
    let count = 0;

    while (true) {
        try {
            const probe = await PDFDocument.create();
            await probe.copyPages(pdf, [count]);
            count++;
        } catch {
            break;
        }
    }

    return count;
}

async function getPdfPageCount(pdf) {
    try {
        return pdf.getPageCount();
    } catch {
        const inferred = await inferPageCountByCopying(pdf);

        if (!inferred) {
            throw new Error("Unable to read PDF page count");
        }

        return inferred;
    }
}

export default function SplitDocumentModal({
    open,
    sourceDoc,
    onClose,
    onExtract,
    onDelete
}) {

    const [numPages, setNumPages] = useState(0);
    const [selectedPage, setSelectedPage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const tiffCanvasRef = useRef(null);

    const isPdf = sourceDoc?.type?.includes("pdf");
    const isTiff = sourceDoc?.type?.includes("tif");
    const isImage = sourceDoc?.type?.startsWith("image/") && !isTiff;

    const src = sourceDoc?.url; // always S3 presigned URL

    const [snack, setSnack] = useState({
        open: false,
        message: "",
        severity: "info"
    });

    /* ---------------- LOAD METADATA ---------------- */

    useEffect(() => {
        if (!open || !sourceDoc || !src) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setNumPages(0);
            setSelectedPage(null);

            if (isPdf) {
                try {
                    const bytes = await fetchPdfBytes(src);
                    if (cancelled) return;

                    const pdf = await PDFDocument.load(bytes);

                    const pageCount = await getPdfPageCount(pdf);

                    console.log("get pages...", pageCount)
                    setNumPages(pageCount);
                    setSelectedPage(1);
                    
                } catch (err) {
                    console.error(err);
                    setSnack({
                        open: true,
                        severity: "error",
                        message: "Document link expired. Please reopen the document."
                    });
                    onClose();
                }
            }

            if (isTiff) {
                const buffer = await fetch(src).then(r => r.arrayBuffer());
                if (cancelled) return;

                const tiff = new Tiff({ buffer });
                setNumPages(tiff.countDirectory());
                setSelectedPage(1); // auto select
            }

            setLoading(false);
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [open, sourceDoc, src]);

    /* ---------------- TIFF RENDER ---------------- */

    useEffect(() => {
        if (!isTiff || !selectedPage || !src) return;
        fetch(src)
            .then(r => r.arrayBuffer())
            .then(buffer => {
                const tiff = new Tiff({ buffer });
                tiff.setDirectory(selectedPage - 1);
                const canvas = tiff.toCanvas();

                if (tiffCanvasRef.current) {
                    tiffCanvasRef.current.innerHTML = "";
                    tiffCanvasRef.current.appendChild(canvas);
                }
            });
    }, [selectedPage, src]);

    /* ---------------- PDF SPLIT ---------------- */

    const splitPdf = async () => {
        if (!src || selectedPage == null) {
            throw new Error("Missing source or selected page");
        }

        const pageIndex = selectedPage - 1;

        const bytes = await fetchPdfBytes(src);
        const srcPdf = await PDFDocument.load(bytes);
        const totalPages = await getPdfPageCount(srcPdf);

        const extractedPdf = await PDFDocument.create();
        const remainingPdf = await PDFDocument.create();

        for (let i = 0; i < totalPages; i++) {
            if (i === pageIndex) {
                // copy ONLY into extractedPdf
                const [page] = await extractedPdf.copyPages(srcPdf, [i]);
                extractedPdf.addPage(page);
            } else {
                // copy ONLY into remainingPdf
                const [page] = await remainingPdf.copyPages(srcPdf, [i]);
                remainingPdf.addPage(page);
            }
        }

        const extractedFile = new File(
            [await extractedPdf.save()],
            `EXTRACTED_${sourceDoc?.originalFilename}`,
            { type: "application/pdf" }
        );

        const remainingFile =
            remainingPdf.getPageCount() > 0
                ? new File(
                    [await remainingPdf.save()],
                    sourceDoc?.originalFilename,
                    { type: "application/pdf" }
                )
                : null;

        return { extracted: extractedFile, remaining: remainingFile };
    };


    /* ---------------- HANDLER ---------------- */

    const handleExtract = async () => {
        if (isImage) {
            handleSplit({
                extracted: sourceDoc.file,
                remaining: null
            });
            return;
        }

        if (isPdf && selectedPage) {
            try {
                const result = await splitPdf();
                handleSplit(result);
            } catch (err) {
                console.error(err);
                setSnack({
                    open: true,
                    severity: "error",
                    message: err.message || "Failed to extract document"
                });
            }
            return;
        }

        if (isTiff && selectedPage) {
            handleSplit({
                extracted: { url: src, page: selectedPage },
                remaining: { url: src, excludePage: selectedPage }
            });
        }
    };

    const handleSplit = async (files) => {
        const splitFilesArray = normalizeSplitFiles(files);

        setSnack({
            open: true,
            severity: 'info',
            message: 'Uploading extracted documents...'
        });

        const rows = Array.from(splitFilesArray).map((file) => ({
            id: sourceDoc.docId,
            name: file.name,
            docTypeCode: sourceDoc.docTypeCode,
            type: file.type,
            file: file.file,
            docTypeId: sourceDoc.docTypeId
        }));

        const formData = new FormData();
        rows.forEach((doc, index) => {
            formData.append(`documents[${index}][id]`, doc.id);
            formData.append(`documents[${index}][controlNo]`, sourceDoc.controlNumber);
            formData.append(`documents[${index}][docTypeCode]`, doc.docTypeCode);
            formData.append(`documents[${index}][docTypeId]`, doc.docTypeId);
            formData.append(`documents[${index}][files]`, doc.file);
        });

        setIsSaving(true);
        uploadDocuments(sourceDoc.locationNumber, formData).then(async (res) => {
            if (res?.status === 'success') {
                await deleteDocument(sourceDoc.locationNumber, sourceDoc.docId);
                setSnack({
                    open: true,
                    severity: 'success',
                    message: 'Extracted documents have been uploaded successfully!'
                });
                const extractedRecord = res?.data?.uploadedFiles?.find(
                    (item) => item?.fileAttachedByUser?.startsWith('EXTRACTED')
                );
                onExtract(true, sourceDoc,extractedRecord?.fileName);
                setIsSaving(false);

            } else {
                onExtract(false, sourceDoc);
                setIsSaving(false);
                setSnack({
                    open: true,
                    severity: 'error',
                    message: 'Failed to upload documents. Please try again.'
                });
            }
        })

    }

    if (!sourceDoc) return null;

    const deletePdfPage = async () => {
        if (!src || selectedPage == null) {
            throw new Error("Missing source or selected page");
        }

        const pageIndex = selectedPage - 1;

        const bytes = await fetchPdfBytes(src);
        const srcPdf = await PDFDocument.load(bytes);
        const totalPages = await getPdfPageCount(srcPdf);

        if (totalPages === 1) {
            throw new Error("Cannot delete the only page in a document");
        }

        const recreatedPdf = await PDFDocument.create();

        for (let i = 0; i < totalPages; i++) {
            if (i !== pageIndex) {
                const [page] = await recreatedPdf.copyPages(srcPdf, [i]);
                recreatedPdf.addPage(page);
            }
        }

        return new File(
            [await recreatedPdf.save()],
            sourceDoc.originalFilename,
            { type: "application/pdf" }
        );
    };
    const handleDeletePage = async () => {
        try {
            setConfirmDelete(false);
            setIsSaving(true);

            const recreatedFile = await deletePdfPage();

            setSnack({
                open: true,
                severity: "info",
                message: "Recreating document..."
            });

            const formData = new FormData();
            formData.append(`documents[0][id]`, sourceDoc.docId);
            formData.append(`documents[0][controlNo]`, sourceDoc.controlNumber);
            formData.append(`documents[0][docTypeCode]`, sourceDoc.docTypeCode);
            formData.append(`documents[0][docTypeId]`, sourceDoc.docTypeId);
            formData.append(`documents[0][files]`, recreatedFile);

            const res = await uploadDocuments(sourceDoc.locationNumber, formData);

            if (res?.status === "success") {
                await deleteDocument(sourceDoc.locationNumber, sourceDoc.docId);

                setSnack({
                    open: true,
                    severity: "success",
                    message: "Page deleted and document recreated successfully"
                });

                onDelete(true);
            } else {
                throw new Error("Upload failed");
            }
        } catch (err) {
            console.error(err);
            setSnack({
                open: true,
                severity: "error",
                message: err.message || "Failed to delete page"
            });
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
                <DialogContent sx={{ p: 0, height: "80vh" }}>
                    <Box display="flex" flexDirection="column" height="100%">

                        {/* HEADER */}
                        <Box px={2} py={1.5} borderBottom="1px solid #ddd">
                            <Typography variant="h6">Extract Document</Typography>
                            <Typography fontSize={12} color="text.secondary">
                                {isImage ? "Single page document" : "Select a page to extract"}
                            </Typography>
                        </Box>

                        {/* BODY */}
                        <Box display="flex" flex={1} minHeight={0}>

                            {/* THUMBNAILS */}
                            {!isImage && (
                                <Box width={160} borderRight="1px solid #ddd" p={1} overflow="auto">
                                    {loading && <CircularProgress size={20} />}
                                    {Array.from({ length: numPages }).map((_, i) => (
                                        <Thumbnail
                                            key={i}
                                            active={selectedPage === i + 1}
                                            label={`Page ${i + 1}`}
                                            onClick={() => setSelectedPage(i + 1)}
                                        />
                                    ))}
                                </Box>
                            )}

                            {/* PREVIEW */}
                            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                                {isPdf && selectedPage && (
                                    <iframe
                                        key={selectedPage}
                                        src={`${src}#toolbar=0&navpanes=0&scrollbar=1&page=${selectedPage}`}
                                        width="100%"
                                        height="100%"
                                        title="pdf-preview"
                                    />
                                )}

                                {isTiff && selectedPage && <div ref={tiffCanvasRef} />}

                                {isImage && (
                                    <img src={src} alt="preview" style={{ maxWidth: "100%", maxHeight: "70vh" }} />
                                )}
                            </Box>
                        </Box>

                        <Divider />

                        {/* FOOTER */}/
                        <Stack direction="row" justifyContent="space-between" p={2}>
                            {/* LEFT – destructive */}
                            <Button
                                color="error"
                                variant="outlined"
                                disabled={!selectedPage || isSaving}
                                onClick={() => setConfirmDelete(true)}
                            >
                                Delete Selected Page
                            </Button>

                            {/* RIGHT – primary */}
                            <Stack direction="row" spacing={1}>
                                <Button onClick={onClose}>Cancel</Button>
                                <Button
                                    variant="contained"
                                    disabled={(!isImage && !selectedPage) || isSaving}
                                    onClick={handleExtract}
                                    startIcon={isSaving ? <CircularProgress size={16} /> : null}
                                >
                                    Extract & Re-index
                                </Button>
                            </Stack>
                        </Stack>

                        {/* <Stack direction="row" justifyContent="flex-end" spacing={1} p={2}>
                            <Button onClick={onClose}>Cancel</Button>
                            <Button
                                variant="contained"
                                disabled={(!isImage && !selectedPage) || isSaving}
                                onClick={handleExtract}
                                startIcon={isSaving ? <CircularProgress size={16} /> : null}
                            >
                                Extract & Re-index
                            </Button>
                        </Stack> */}
                    </Box>

                </DialogContent>
            </Dialog>
            <ConfirmationDialog
                isConfirmDialogOpen={confirmDelete}
                title={`Delete page ${selectedPage}?`}
                description="This will permanently remove the selected page and recreate the document. This action cannot be undone."
                acceptTxt="Delete Page"
                rejectTxt="Cancel"
                handleNo={() => setConfirmDelete(false)}
                handleYes={handleDeletePage}
            />
            <AppSnackbar
                open={snack.open}
                message={snack.message}
                severity={snack.severity}
                onClose={() =>
                    setSnack({ open: false, message: "", severity: "info" })
                }
            />
        </>
    );
}

/* ---------------- THUMBNAIL ---------------- */

function Thumbnail({ active, label, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                mb: 1,
                cursor: "pointer",
                border: active ? "2px solid #1976d2" : "1px solid #ddd",
                borderRadius: 1,
                p: 1,
                bgcolor: "#fff"
            }}
        >
            <Typography fontSize={12} textAlign="center">
                {label}
            </Typography>
        </Box>
    );
}
